// Lógica de negocio del Constructor de páginas (report-page-builder.vue), separada de la vista.
// Funciones puras / de servicio (sin Vue ni `this`): catálogo de páginas, hidratación,
// auto-enlace, grupos del dashboard, serialización, plantilla en uso, paleta y subida de portada.
// El estado reactivo (pages, selected, _suspendSave, etc.) y el autosave viven en el componente.
import { TYPES, DISCIPLINES, DEFAULT_GROUPS } from '../data/page-builder.render'
import { REPORT_TEMPLATES } from '../data/report-templates.data'
import { isModifiedFrom, matchGroupForDiscipline } from './page-builder.converge'
import { uploadFile } from '·/components/activity-groups/utils/uppyFactory'

// Nombres de grupos de nivel superior (o demo si aún no hay árbol).
export function groupNames(activityGroups) {
  const names = (activityGroups || []).map((g) => g.label || g.name).filter(Boolean)
  return names.length ? names : DEFAULT_GROUPS
}

// Árbol de grupos aplanado: [{ id, label, name, code_custom }].
export function flattenGroups(activityGroups) {
  const out = []
  const walk = (arr) => (arr || []).forEach((n) => {
    out.push({ id: n.id, label: n.label || n.name, name: n.label || n.name, code_custom: n.code_custom || null })
    if (n.children) walk(n.children)
  })
  walk(activityGroups || [])
  return out
}

// Reconstruye una página guardada: defaults del catálogo + ajustes persistidos. `id` lo da el
// componente (contador uid). El File de la portada no se persiste.
export function hydratePage(sp, id, groups) {
  const type = sp && sp.type
  if (!type || !TYPES[type]) return null
  const settings = Object.assign({}, TYPES[type].defaults(groups), sp.settings || {})
  if (type === 'cover') {
    settings.image_cover = Object.assign({ type: 'file', name: null, mimeType: null, url: null, file: null }, (sp.settings && sp.settings.image_cover) || {})
    settings.image_cover.file = null
  }
  return { id, type, visible: sp.visible !== false, settings }
}

export function hydratePages(rawPages, idGen, groups) {
  return (rawPages || []).map((p) => hydratePage(p, idGen(), groups)).filter(Boolean)
}

// Primera disciplina aún no usada por otra página.
export function firstFreeDiscipline(pages) {
  for (let i = 0; i < DISCIPLINES.length; i++) {
    const c = DISCIPLINES[i][0]
    if (!pages.some((p) => p.type === 'discipline' && p.settings.discipline === c)) return c
  }
  return DISCIPLINES[0][0]
}

// Crea una página nueva (defaults del tipo). Para disciplina, `disc` = código elegido.
export function makePage(type, id, groups, disc) {
  const p = { id, type, visible: true, settings: TYPES[type].defaults(groups) }
  if (type === 'discipline') p.settings.discipline = disc || DISCIPLINES[0][0]
  return p
}

// Páginas base por defecto cuando no hay config previa.
export function defaultPages(idGen, groups) {
  return ['cover', 'dashboard', 'groupcharts', 'tasktable'].map((t) => makePage(t, idGen(), groups))
}

// Config traída de otro proyecto: hidrata, suelta portada (por-proyecto) y limpia groupId.
export function prepareImportedPages(rawPages, idGen, groups) {
  const pages = hydratePages(rawPages, idGen, groups)
  pages.forEach((p) => {
    if (p.type === 'cover') p.settings.image_cover = { type: 'file', name: null, mimeType: null, url: null, file: null }
    if (p.type === 'discipline') p.settings.groupId = null
  })
  return pages
}

// Auto-enlace disciplina↔grupo (code_custom + heurística). Muta settings.groupId SOLO cuando
// está nulo (no pisa enlaces); respeta unicidad. Devuelve { pageId: 'code_custom'|'heuristic' }.
export function autoLinkDisciplines(pages, flatGroups) {
  const linkSource = {}
  if (!flatGroups || !flatGroups.length) return linkSource
  const disc = pages.filter((p) => p.type === 'discipline')
  const used = []
  disc.forEach((p) => { if (p.settings.groupId != null) used.push(p.settings.groupId) })
  const run = (mode) => disc.forEach((p) => {
    if (p.settings.groupId != null) return
    const m = matchGroupForDiscipline(p.settings.discipline, flatGroups, used, mode)
    if (m) { p.settings.groupId = m.groupId; used.push(m.groupId); linkSource[p.id] = m.source }
  })
  run('exact')
  run('heuristic')
  return linkSource
}

// IDs de grupo efectivos del dashboard según su modo (parent/children/custom).
export function dashboardGroupIds(pages, activityGroups) {
  const dp = pages.find((p) => p.type === 'dashboard')
  if (!dp) return []
  const ds = dp.settings || {}
  const tree = activityGroups || []
  if (ds.showGroupProgress === 'custom') return (ds.customGroups || []).slice()
  if (!tree.length) return []
  const flat = []
  const walk = (arr) => arr.forEach((n) => { flat.push(n); if (n.children) walk(n.children) })
  walk(tree)
  if (ds.showGroupProgress === 'children') return flat.filter((n) => n.parent_id).map((n) => n.id)
  return tree.filter((n) => !n.parent_id).map((n) => n.id) // parent
}

// Nombres de los grupos fijos del dashboard (para la curva S).
export function dashboardGroupNames(pages, activityGroups, flatGroups) {
  const ids = dashboardGroupIds(pages, activityGroups).map(String)
  return flatGroups.filter((g) => ids.indexOf(String(g.id)) >= 0).map((g) => g.label).filter(Boolean)
}

export function groupchartsExists(pages) { return pages.some((p) => p.type === 'groupcharts') }

// JSON propio del builder (quita el File de la portada; se sube aparte).
export function serialize(pages, templateKey, importedFrom) {
  return {
    version: 1,
    templateKey,
    importedFrom,
    pages: pages.map((p) => ({
      type: p.type,
      visible: p.visible,
      settings: JSON.parse(JSON.stringify(p.settings, (k, v) => (typeof File !== 'undefined' && v instanceof File ? undefined : v))),
    })),
  }
}

// Plantilla en uso (nombre + si difiere de su definición) a partir de templateKey.
export function getActiveTemplate(pages, templateKey) {
  if (!templateKey) return null
  const t = REPORT_TEMPLATES.find((x) => x.key === templateKey)
  if (!t) return null
  return { name: t.name, modified: isModifiedFrom(pages, templateKey) }
}

// Paleta "añadir": deshabilita tipos `single` ya usados y la disciplina si están todas.
// Devuelve [{ key, icon, disabled }]; el nombre traducido lo pone la vista (typeLabel).
export function buildPalette(pages) {
  return Object.keys(TYPES).map((key) => {
    const t = TYPES[key]
    let disabled
    if (key === 'discipline') {
      disabled = DISCIPLINES.every((d) => pages.some((p) => p.type === 'discipline' && p.settings.discipline === d[0]))
    } else {
      disabled = !!t.single && pages.some((p) => p.type === key)
    }
    return { key, icon: t.icon, disabled }
  })
}

// Sube a S3 (uppy) la imagen de portada pendiente (File). NO muta: devuelve los datos para
// que el componente los aplique bajo su guard _suspendSave, o null si no hay nada que subir.
export async function uploadCover(pages) {
  const cover = pages.find((p) => p.type === 'cover')
  const ic = cover && cover.settings && cover.settings.image_cover
  if (!ic || !ic.file) return null
  const { file, response } = await uploadFile(ic.file)
  return { ic, name: file.name, mimeType: file.type, key: response.body.key }
}
